/**
 * Procedural sound ambience for the 3D viewer — opt-in, and deliberately
 * almost subliminal.
 *
 * The repo ships no audio assets, so every layer is synthesised with the
 * WebAudio API:
 *
 *  1. **Wind bed** — two decorrelated loops of the same brown-noise buffer,
 *     panned wide, through a highpass (rumble) and a lowpass whose cutoff and
 *     level each breathe under their own slow LFO (16 s and 11 s, started at
 *     different times so the two never lock into one pulse).
 *  2. **Wood crackle** — only for a wood-fired heater. Short noise impulses
 *     through a randomised bandpass with a fast decay, fired in small bursts
 *     at random 0.3-3 s gaps. Muted and mid-heavy, like a closed stove across
 *     the room.
 *
 * A third layer (a distant bird) was scoped but is deliberately NOT shipped —
 * see the note at the bottom of this file.
 *
 * ## Contracts this module must keep
 *
 * - **No React, no Three.js.** The `AudioContext` factory is injectable so the
 *   whole graph can be unit-tested with a mock (jsdom has no WebAudio).
 * - **Nothing here may throw when WebAudio is missing.** Every entry point is
 *   defensive; `isViewerAmbienceSupported()` lets the UI hide the control
 *   entirely instead of offering a dead button.
 * - **Never touches the R3F demand frameloop.** No `useFrame`, no
 *   `invalidate()`, no rAF: WebAudio runs on its own clock and the random
 *   crackle is scheduled with `setTimeout` + `AudioContext.currentTime`
 *   (see docs/notes/2026-04-11-demand-mode-viewers-must-gate-idle-ambience-loops.md).
 * - **No zipper noise.** Every runtime parameter change goes through
 *   `setTargetAtTime` or an explicit ramp — never a direct `.value` write on a
 *   live graph.
 */

import { clampTimeOfDay } from "../../domain/viewer/timeOfDay";

export type AmbienceHeaterType = "wood" | "electric";

/**
 * Outcome of `start()`. A boolean cannot carry this: "the user changed their
 * mind while `resume()` was in flight" and "this device will not play audio"
 * look identical from the outside, and only the second may be allowed to drop
 * the user's stored preference.
 */
export type ViewerAmbienceStartResult =
	/** Audio is running (or deliberately parked because the tab is hidden). */
	| "started"
	/** Superseded by a later start, or stopped/disposed mid-flight. */
	| "cancelled"
	/** No context, or the browser refused to leave the suspended state. */
	| "unavailable";

export interface ViewerAmbienceOptions {
	/** Injectable so tests can supply a mock graph; defaults to the platform ctor. */
	createContext?: () => AudioContext;
	/** Injectable timers keep the crackle scheduler testable without fake timers. */
	setTimeoutFn?: (handler: () => void, timeoutMs: number) => number;
	clearTimeoutFn?: (id: number) => void;
	/** Injectable RNG so crackle timing/timbre is deterministic under test. */
	random?: () => number;
	heaterType?: AmbienceHeaterType;
	timeOfDay?: number;
}

/** Fade applied on start/stop so the toggle never clicks. */
const FADE_S = 1;
/**
 * Teardown fade. Short — an unmount should not be perceptibly delayed — but a
 * hard `stop()` on a live master is an audible click, which the module's
 * no-zipper-noise invariant forbids just as much as a parameter jump does.
 */
const DISPOSE_FADE_S = 0.12;
/**
 * Master level. The ambience is meant to sit under conscious attention — it
 * should read as "the room is not dead", not as a soundtrack.
 */
const MASTER_GAIN = 0.18;

const WIND_BASE_LEVEL = 0.5;
const WIND_LEVEL_LFO_DEPTH = 0.22;
const WIND_LEVEL_LFO_PERIOD_S = 11;
/** Start delay = phase offset, so the two LFOs never crest together. */
const WIND_LEVEL_LFO_PHASE_DELAY_S = 2.7;
const WIND_CUTOFF_DAY_HZ = 520;
/** How far the bed darkens between midday and night. */
const WIND_CUTOFF_NIGHT_DROP_HZ = 170;
const WIND_CUTOFF_LFO_DEPTH_HZ = 150;
const WIND_CUTOFF_LFO_PERIOD_S = 16;
/** How much quieter the bed gets at night. */
const WIND_NIGHT_LEVEL_DROP = 0.35;
const WIND_HIGHPASS_HZ = 80;
const WIND_LOWPASS_Q = 0.6;
/**
 * Length of the looping noise bed, in seconds.
 *
 * The buffer is synthesised synchronously inside the click handler that
 * starts the audio, so its length is main-thread blocking time: 6 s measured
 * ~16 ms on desktop and an estimated 60-100 ms on a mid-range phone, for
 * ~2.5 MB of transient typed arrays. 3 s halves that while staying long
 * enough that the loop seam is inaudible under two decorrelated playback
 * rates, random entry offsets and the 16 s / 11 s LFOs.
 */
const NOISE_BUFFER_S = 3;
/** The loop tail is crossfaded into its head so the seam has no step. */
const NOISE_CROSSFADE_S = 0.5;
const NOISE_PEAK = 0.9;
/** Second voice runs slower, so the two loops never phase-lock. */
const WIND_VOICE_RATES = [1, 0.83];
const WIND_VOICE_PANS = [-0.6, 0.62];

const CRACKLE_GAIN = 0.9;
const CRACKLE_BUFFER_S = 0.3;
const CRACKLE_MIN_GAP_S = 0.3;
const CRACKLE_MAX_GAP_S = 3;
const CRACKLE_MAX_POPS_PER_BURST = 3;
const CRACKLE_POP_SPACING_MIN_S = 0.02;
const CRACKLE_POP_SPACING_RANGE_S = 0.07;
const CRACKLE_MIN_AMP = 0.1;
const CRACKLE_AMP_RANGE = 0.32;
const CRACKLE_MIN_HZ = 900;
const CRACKLE_HZ_RANGE = 3100;
const CRACKLE_MIN_DECAY_S = 0.035;
const CRACKLE_DECAY_RANGE_S = 0.14;
const CRACKLE_ATTACK_S = 0.003;
/** Lookahead so an envelope is never scheduled in the past. */
const CRACKLE_LOOKAHEAD_S = 0.02;

/** Time constant for every live parameter move — long enough to be inaudible. */
const PARAM_SMOOTHING_S = 0.35;
/** Time-of-day value at which the bed starts settling towards its night state. */
const NIGHTFALL_START = 0.45;

/**
 * Generic 0-1 clamp for derived ratios. Time-of-day itself goes through
 * `clampTimeOfDay`, the domain's canonical normaliser for that value.
 */
function clamp01(value: number): number {
	if (!Number.isFinite(value)) return 0;
	return value < 0 ? 0 : value > 1 ? 1 : value;
}

type AudioContextCtor = new () => AudioContext;

function resolveAudioContextCtor(): AudioContextCtor | null {
	if (typeof window === "undefined") return null;
	const scope = window as unknown as {
		AudioContext?: AudioContextCtor;
		webkitAudioContext?: AudioContextCtor;
	};
	const ctor = scope.AudioContext ?? scope.webkitAudioContext;
	return typeof ctor === "function" ? ctor : null;
}

/**
 * Whether this device can play the ambience at all. The UI hides the toggle
 * when this is false rather than offering a control that can only fail.
 */
export function isViewerAmbienceSupported(): boolean {
	return resolveAudioContextCtor() !== null;
}

function defaultCreateContext(): AudioContext {
	const Ctor = resolveAudioContextCtor();
	if (!Ctor) throw new Error("WebAudio is unavailable");
	return new Ctor();
}

/** SSR-safe: a hidden tab must not be handed a running audio graph. */
function isDocumentHidden(): boolean {
	if (typeof document === "undefined") return false;
	return document.visibilityState === "hidden";
}

/** Best-effort cleanup helper: teardown must never surface an exception. */
function quietly(action: () => void): void {
	try {
		action();
	} catch {
		// A node may already be disconnected or the context already closed.
	}
}

export class ViewerAmbience {
	private readonly createContext: () => AudioContext;
	private readonly setTimeoutFn: (
		handler: () => void,
		timeoutMs: number,
	) => number;
	private readonly clearTimeoutFn: (id: number) => void;
	private readonly random: () => number;

	private ctx: AudioContext | null = null;
	private master: GainNode | null = null;
	private windLevel: GainNode | null = null;
	private windFilter: BiquadFilterNode | null = null;
	private crackleBus: GainNode | null = null;
	private crackleBuffer: AudioBuffer | null = null;
	private sources: AudioScheduledSourceNode[] = [];
	/** Every persistent node, so teardown disconnects the whole graph. */
	private graphNodes: AudioNode[] = [];

	private crackleTimer: number | null = null;
	private suspendTimer: number | null = null;
	private disposeTimer: number | null = null;

	private heaterType: AmbienceHeaterType;
	private timeOfDay: number;
	private running = false;
	private disposed = false;
	/**
	 * Generation counter for `start()`. `await ctx.resume()` can take well over
	 * 100 ms on a cold audio device, and anything the user does in that window
	 * (toggling back off, unmounting) must win — otherwise the ambience fades
	 * IN behind a button that already reads "off".
	 */
	private startToken = 0;

	constructor(options: ViewerAmbienceOptions = {}) {
		this.createContext = options.createContext ?? defaultCreateContext;
		this.setTimeoutFn =
			options.setTimeoutFn ??
			((handler, timeoutMs) =>
				setTimeout(handler, timeoutMs) as unknown as number);
		this.clearTimeoutFn = options.clearTimeoutFn ?? ((id) => clearTimeout(id));
		this.random = options.random ?? Math.random;
		this.heaterType = options.heaterType ?? "electric";
		this.timeOfDay = clampTimeOfDay(options.timeOfDay ?? 0);
	}

	/** True while the user has the ambience switched on. */
	get isRunning(): boolean {
		return this.running;
	}

	/**
	 * Bring the ambience up. MUST be called from a user gesture (or after one):
	 * browsers only let an `AudioContext` be created/resumed with sticky user
	 * activation. Only an `"unavailable"` result means the caller should drop
	 * the toggle back off — `"cancelled"` is a straggler whose intent has
	 * already been replaced.
	 */
	async start(): Promise<ViewerAmbienceStartResult> {
		if (this.disposed) return "cancelled";
		this.cancelPendingSuspend();
		// Claim this attempt before the first await. A later `stop()`/`dispose()`
		// bumps the token, which is the only way to tell "the user still wants
		// sound" from "this promise is a straggler".
		const token = ++this.startToken;
		try {
			if (!this.ctx) this.ctx = this.createContext();
			const ctx = this.ctx;
			if (!ctx) return "unavailable";
			this.buildGraph();
			if (ctx.state === "suspended") await ctx.resume();
			if (this.disposed || token !== this.startToken) return "cancelled";
			if (isDocumentHidden()) {
				// The tab went away while `resume()` was in flight, so its
				// `visibilitychange` has already fired and nothing else will park
				// this. Keep the user's intent (`running`) so the next
				// `visibilitychange` resumes it; the master ramp is scheduled on a
				// frozen clock and plays out when it does.
				this.running = true;
				this.rampMaster(MASTER_GAIN);
				this.suspend();
				return "started";
			}
			if (ctx.state === "suspended") {
				// `resume()` settled but the context never started — autoplay is
				// still blocking. Do not claim to be playing.
				return "unavailable";
			}
			this.running = true;
			this.rampMaster(MASTER_GAIN);
			this.startCrackleScheduler();
			return "started";
		} catch {
			// A blocked or unsupported context must never break the viewer.
			this.teardownAudio();
			return "unavailable";
		}
	}

	/**
	 * Fade out and park the context. The graph is kept so a re-toggle is
	 * instant; the context is suspended once the fade has finished so a silent
	 * viewer costs no audio thread.
	 */
	stop(): void {
		// Invalidate any start still waiting on `resume()`.
		this.startToken += 1;
		this.running = false;
		this.stopCrackleScheduler();
		const ctx = this.ctx;
		if (!ctx || !this.master) return;
		this.rampMaster(0);
		this.cancelPendingSuspend();
		this.suspendTimer = this.setTimeoutFn(
			() => {
				this.suspendTimer = null;
				if (this.running) return;
				this.suspend();
			},
			(FADE_S + 0.15) * 1000,
		);
	}

	/** Park the audio thread — used when the tab is hidden. */
	suspend(): void {
		// `setTimeout` keeps firing in a hidden tab while `currentTime` is
		// frozen, so leaving the scheduler armed would queue every pop at the
		// same instant and dump them all at once on resume.
		this.stopCrackleScheduler();
		const ctx = this.ctx;
		if (!ctx || this.disposed) return;
		if (ctx.state !== "running") return;
		quietly(() => {
			void ctx.suspend().catch(() => {});
		});
	}

	/**
	 * Resume after the tab becomes visible again, but only when the user
	 * actually has the ambience switched on.
	 */
	resume(): void {
		const ctx = this.ctx;
		if (!ctx || this.disposed || !this.running) return;
		// `suspend()` is asynchronous: a fast hidden -> visible flip lands here
		// while the state still reads "running", and gating on
		// `state === "suspended"` would skip the resume and leave the ambience
		// permanently parked behind a lit button. `resume()` is idempotent, so
		// call it unconditionally on any context that is still open.
		if (ctx.state === "closed") return;
		quietly(() => {
			void ctx.resume().catch(() => {});
		});
		this.startCrackleScheduler();
	}

	/**
	 * Wood <-> electric while the ambience is playing: the crackle bus fades
	 * rather than cutting, and the scheduler stops immediately so no new pop is
	 * queued behind the fade.
	 */
	setHeaterType(next: AmbienceHeaterType): void {
		if (next === this.heaterType) return;
		this.heaterType = next;
		const ctx = this.ctx;
		if (!ctx || !this.crackleBus) return;
		this.crackleBus.gain.setTargetAtTime(
			next === "wood" ? CRACKLE_GAIN : 0,
			ctx.currentTime,
			PARAM_SMOOTHING_S,
		);
		if (next === "wood") {
			this.startCrackleScheduler();
		} else {
			this.stopCrackleScheduler();
		}
	}

	/** Midday -> night: the bed gets quieter and darker, never abruptly. */
	setTimeOfDay(next: number): void {
		this.timeOfDay = clampTimeOfDay(next);
		this.applyTimeOfDay();
	}

	/**
	 * Stop everything and close the context. The instance is dead afterwards.
	 *
	 * A live graph is faded out over `DISPOSE_FADE_S` first: cutting the master
	 * with `source.stop()` + `ctx.close()` is an audible click on every
	 * workspace switch, which is the same defect as a parameter jump.
	 */
	dispose(): void {
		if (this.disposed) return;
		// Ask the master where it actually is, not what the user last intended:
		// `stop()` clears `running` but leaves a 1 s ramp in flight, so an
		// unmount inside that window (toggle off, switch workspace) would still
		// have cut a ~0.7 x MASTER_GAIN signal dead.
		const wasAudible =
			this.ctx?.state === "running" && (this.master?.gain.value ?? 0) > 0.0001;
		this.disposed = true;
		this.running = false;
		this.startToken += 1;
		this.stopCrackleScheduler();
		this.cancelPendingSuspend();
		if (!wasAudible || !this.master) {
			this.teardownAudio();
			return;
		}
		this.rampMaster(0, DISPOSE_FADE_S);
		this.disposeTimer = this.setTimeoutFn(() => {
			this.disposeTimer = null;
			this.teardownAudio();
		}, DISPOSE_FADE_S * 1000);
	}

	// ── graph ────────────────────────────────────────────────────────────────

	private buildGraph(): void {
		const ctx = this.ctx;
		if (!ctx || this.master) return;

		const master = ctx.createGain();
		master.gain.value = 0;
		master.connect(ctx.destination);
		this.master = master;
		this.graphNodes.push(master);

		this.buildWindLayer(ctx, master);
		this.buildCrackleBus(ctx, master);
		this.applyTimeOfDay();
	}

	private buildWindLayer(ctx: AudioContext, master: GainNode): void {
		const highpass = ctx.createBiquadFilter();
		highpass.type = "highpass";
		highpass.frequency.value = WIND_HIGHPASS_HZ;

		const lowpass = ctx.createBiquadFilter();
		lowpass.type = "lowpass";
		lowpass.frequency.value = WIND_CUTOFF_DAY_HZ;
		lowpass.Q.value = WIND_LOWPASS_Q;

		const level = ctx.createGain();
		level.gain.value = WIND_BASE_LEVEL;

		highpass.connect(lowpass);
		lowpass.connect(level);
		level.connect(master);
		this.graphNodes.push(highpass, lowpass, level);

		const buffer = this.createWindBuffer(ctx);
		const now = ctx.currentTime;
		WIND_VOICE_RATES.forEach((rate, index) => {
			const source = ctx.createBufferSource();
			source.buffer = buffer;
			source.loop = true;
			source.playbackRate.value = rate;
			const panner = this.createPanner(ctx, WIND_VOICE_PANS[index] ?? 0);
			if (panner) {
				source.connect(panner);
				panner.connect(highpass);
				this.graphNodes.push(panner);
			} else {
				source.connect(highpass);
			}
			// Each voice enters at its own point in the loop, so the two are
			// decorrelated from the very first second.
			source.start(now, this.random() * NOISE_BUFFER_S);
			this.sources.push(source);
		});

		this.startLfo(ctx, {
			periodS: WIND_CUTOFF_LFO_PERIOD_S,
			depth: WIND_CUTOFF_LFO_DEPTH_HZ,
			target: lowpass.frequency,
			startDelayS: 0,
		});
		this.startLfo(ctx, {
			periodS: WIND_LEVEL_LFO_PERIOD_S,
			depth: WIND_LEVEL_LFO_DEPTH,
			target: level.gain,
			startDelayS: WIND_LEVEL_LFO_PHASE_DELAY_S,
		});

		this.windFilter = lowpass;
		this.windLevel = level;
	}

	private buildCrackleBus(ctx: AudioContext, master: GainNode): void {
		const bus = ctx.createGain();
		bus.gain.value = this.heaterType === "wood" ? CRACKLE_GAIN : 0;
		bus.connect(master);
		this.graphNodes.push(bus);
		this.crackleBus = bus;
		this.crackleBuffer = this.createCrackleBuffer(ctx);
	}

	/**
	 * `StereoPannerNode` is not universally available (older Safari); without
	 * it the bed simply plays mono rather than failing.
	 */
	private createPanner(
		ctx: AudioContext,
		pan: number,
	): StereoPannerNode | null {
		if (typeof ctx.createStereoPanner !== "function") return null;
		try {
			const panner = ctx.createStereoPanner();
			panner.pan.value = pan;
			return panner;
		} catch {
			return null;
		}
	}

	private startLfo(
		ctx: AudioContext,
		options: {
			periodS: number;
			depth: number;
			target: AudioParam;
			startDelayS: number;
		},
	): void {
		const osc = ctx.createOscillator();
		osc.type = "sine";
		osc.frequency.value = 1 / options.periodS;
		const depth = ctx.createGain();
		depth.gain.value = options.depth;
		osc.connect(depth);
		// Connecting to an AudioParam SUMS with the param's own value, so the
		// time-of-day offset below and this modulation coexist.
		depth.connect(options.target);
		// A sine starts at zero amplitude, so a delayed start is a pure phase
		// offset — it can never introduce a step.
		osc.start(ctx.currentTime + options.startDelayS);
		this.sources.push(osc);
		this.graphNodes.push(depth);
	}

	private applyTimeOfDay(): void {
		const ctx = this.ctx;
		if (!ctx || !this.windLevel || !this.windFilter) return;
		const nightness = clamp01(
			(this.timeOfDay - NIGHTFALL_START) / (1 - NIGHTFALL_START),
		);
		const now = ctx.currentTime;
		this.windLevel.gain.setTargetAtTime(
			WIND_BASE_LEVEL * (1 - WIND_NIGHT_LEVEL_DROP * nightness),
			now,
			PARAM_SMOOTHING_S,
		);
		this.windFilter.frequency.setTargetAtTime(
			WIND_CUTOFF_DAY_HZ - WIND_CUTOFF_NIGHT_DROP_HZ * nightness,
			now,
			PARAM_SMOOTHING_S,
		);
	}

	private rampMaster(target: number, durationS = FADE_S): void {
		const ctx = this.ctx;
		const master = this.master;
		if (!ctx || !master) return;
		const now = ctx.currentTime;
		const gain = master.gain;
		// Cancel-then-anchor at the current value, so a toggle mid-fade turns
		// around from where it actually is instead of jumping.
		gain.cancelScheduledValues(now);
		gain.setValueAtTime(gain.value, now);
		gain.linearRampToValueAtTime(target, now + durationS);
	}

	// ── crackle ──────────────────────────────────────────────────────────────

	private startCrackleScheduler(): void {
		if (this.disposed || !this.running) return;
		if (this.heaterType !== "wood") return;
		if (this.crackleTimer !== null) return;
		if (!this.ctx || !this.crackleBus) return;
		this.queueNextCrackle();
	}

	private queueNextCrackle(): void {
		const gapS =
			CRACKLE_MIN_GAP_S +
			this.random() * (CRACKLE_MAX_GAP_S - CRACKLE_MIN_GAP_S);
		this.crackleTimer = this.setTimeoutFn(() => {
			this.crackleTimer = null;
			if (this.disposed || !this.running || this.heaterType !== "wood") return;
			this.emitCrackleBurst();
			this.queueNextCrackle();
		}, gapS * 1000);
	}

	private stopCrackleScheduler(): void {
		if (this.crackleTimer === null) return;
		this.clearTimeoutFn(this.crackleTimer);
		this.crackleTimer = null;
	}

	/**
	 * Real wood does not tick like a metronome — it goes quiet, then lets off
	 * two or three knocks in quick succession. One pop per wake-up would read
	 * as a fault in the page, not as a fire.
	 */
	private emitCrackleBurst(): void {
		const ctx = this.ctx;
		if (!ctx || !this.crackleBus || !this.crackleBuffer) return;
		const pops = 1 + Math.floor(this.random() * CRACKLE_MAX_POPS_PER_BURST);
		let when = ctx.currentTime + CRACKLE_LOOKAHEAD_S;
		for (let index = 0; index < pops; index += 1) {
			this.emitCracklePop(ctx, when);
			when +=
				CRACKLE_POP_SPACING_MIN_S + this.random() * CRACKLE_POP_SPACING_RANGE_S;
		}
	}

	private emitCracklePop(ctx: AudioContext, when: number): void {
		const bus = this.crackleBus;
		const buffer = this.crackleBuffer;
		if (!bus || !buffer) return;

		const amp = CRACKLE_MIN_AMP + this.random() * CRACKLE_AMP_RANGE;
		const decayS = CRACKLE_MIN_DECAY_S + this.random() * CRACKLE_DECAY_RANGE_S;

		const source = ctx.createBufferSource();
		source.buffer = buffer;
		source.playbackRate.value = 0.7 + this.random() * 0.8;

		const band = ctx.createBiquadFilter();
		band.type = "bandpass";
		band.frequency.value = CRACKLE_MIN_HZ + this.random() * CRACKLE_HZ_RANGE;
		band.Q.value = 2.5 + this.random() * 5;

		const envelope = ctx.createGain();
		envelope.gain.setValueAtTime(0, when);
		envelope.gain.linearRampToValueAtTime(amp, when + CRACKLE_ATTACK_S);
		// Exponential tail: a linear one to zero reads as a synthetic blip.
		envelope.gain.exponentialRampToValueAtTime(0.0005, when + decayS);

		source.connect(band);
		band.connect(envelope);
		envelope.connect(bus);

		source.onended = () => {
			quietly(() => source.disconnect());
			quietly(() => band.disconnect());
			quietly(() => envelope.disconnect());
		};
		const offset = this.random() * (CRACKLE_BUFFER_S / 2);
		source.start(when, offset, decayS + 0.05);
	}

	// ── buffers ──────────────────────────────────────────────────────────────

	/**
	 * Brown noise (integrated white), normalised, with its tail crossfaded into
	 * its head. Without that crossfade the loop point is a step in a very
	 * low-frequency signal, which the highpass turns into an audible thump
	 * every six seconds.
	 */
	private createWindBuffer(ctx: AudioContext): AudioBuffer {
		const sampleRate = ctx.sampleRate || 44100;
		const length = Math.max(1, Math.floor(sampleRate * NOISE_BUFFER_S));
		const fade = Math.max(1, Math.floor(sampleRate * NOISE_CROSSFADE_S));
		const raw = new Float32Array(length + fade);
		let last = 0;
		let peak = 0;
		for (let index = 0; index < raw.length; index += 1) {
			const white = this.random() * 2 - 1;
			last = (last + 0.02 * white) / 1.02;
			const sample = last * 3.5;
			raw[index] = sample;
			const magnitude = Math.abs(sample);
			if (magnitude > peak) peak = magnitude;
		}
		for (let index = 0; index < fade; index += 1) {
			const weight = index / fade;
			raw[index] = raw[index] * weight + raw[length + index] * (1 - weight);
		}
		const scale = peak > 0 ? NOISE_PEAK / peak : 1;
		const buffer = ctx.createBuffer(1, length, sampleRate);
		const channel = buffer.getChannelData(0);
		for (let index = 0; index < length; index += 1) {
			channel[index] = raw[index] * scale;
		}
		return buffer;
	}

	/** White noise; the per-pop bandpass is what gives each knock its timbre. */
	private createCrackleBuffer(ctx: AudioContext): AudioBuffer {
		const sampleRate = ctx.sampleRate || 44100;
		const length = Math.max(1, Math.floor(sampleRate * CRACKLE_BUFFER_S));
		const buffer = ctx.createBuffer(1, length, sampleRate);
		const channel = buffer.getChannelData(0);
		for (let index = 0; index < length; index += 1) {
			channel[index] = this.random() * 2 - 1;
		}
		return buffer;
	}

	// ── teardown ─────────────────────────────────────────────────────────────

	private cancelPendingSuspend(): void {
		if (this.suspendTimer === null) return;
		this.clearTimeoutFn(this.suspendTimer);
		this.suspendTimer = null;
	}

	private teardownAudio(): void {
		if (this.disposeTimer !== null) {
			this.clearTimeoutFn(this.disposeTimer);
			this.disposeTimer = null;
		}
		for (const source of this.sources) {
			quietly(() => source.stop());
			quietly(() => source.disconnect());
		}
		this.sources = [];
		// Every persistent node, not just the master: a graph left half-wired
		// keeps the intermediate nodes referenced by the (closing) context.
		for (const node of this.graphNodes) {
			quietly(() => node.disconnect());
		}
		this.graphNodes = [];
		const ctx = this.ctx;
		this.ctx = null;
		this.master = null;
		this.windLevel = null;
		this.windFilter = null;
		this.crackleBus = null;
		this.crackleBuffer = null;
		this.running = false;
		if (!ctx) return;
		quietly(() => {
			const closing = ctx.close();
			if (closing && typeof closing.catch === "function") {
				closing.catch(() => {});
			}
		});
	}
}

/*
 * Why there is no third layer
 * ---------------------------
 * The brief offered an optional daytime bird call (an FM blip on a 20-60 s
 * timer). It is skipped on purpose: a synthesised bird is the single easiest
 * way to make an ambience sound cheap, and nothing in this environment can
 * audition it. Two layers that are certainly unobtrusive beat three where the
 * third might read as a modem. Time-of-day still modulates the bed (quieter
 * and darker towards night), which is the part of that idea that survives
 * without an audition.
 */
